import { AnimationClip, Matrix4, Mesh, Object3D, PropertyBinding, SkinnedMesh } from "three";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js";
import { AnimationPlayback } from "./AnimationPlayback";
/**
 * Animates an imported scene piece without duplicating the other scene visuals.
 * The hidden source retains ancestor transforms and external skeleton bones.
 */
export class MeshPieceAnimation {
    constructor(root, animatedPiece, clips, inverseBindTransform, clipIds, clipOccurrences) {
        this.animatedPiece = animatedPiece;
        this.context = new Object3D();
        this.inverseParent = new Matrix4();
        this.mesh = animatedPiece.clone(false);
        this.mesh.position.setScalar(0);
        this.mesh.quaternion.identity();
        this.mesh.scale.setScalar(1);
        this.mesh.matrixAutoUpdate = false;
        this.mesh.animations = clips;
        this.context.visible = false;
        this.context.matrixAutoUpdate = false;
        this.context.matrix.copy(inverseBindTransform);
        this.context.add(root);
        root.traverse((node) => {
            node.matrixAutoUpdate = true;
            node.layers.disableAll();
        });
        this.playback = new AnimationPlayback(root, clips, clipIds, clipOccurrences);
    }
    static create(root, piece) {
        const relatedNodes = new Set();
        const addAncestors = (node) => {
            for (let current = node; current; current = current.parent) {
                relatedNodes.add(current);
                if (current === root)
                    break;
            }
        };
        addAncestors(piece);
        if (piece instanceof SkinnedMesh) {
            for (const bone of piece.skeleton.bones) {
                addAncestors(bone);
            }
        }
        const relevantClips = root.animations.map((clip) => ({
            clip,
            tracks: clip.tracks.map((track) => {
                var _a;
                const parsed = PropertyBinding.parseTrackName(track.name);
                let target = PropertyBinding.findNode(root, parsed.nodeName);
                if (parsed.objectName === "bones" && target instanceof SkinnedMesh) {
                    target = (_a = target.skeleton.getBoneByName(parsed.objectIndex)) !== null && _a !== void 0 ? _a : target.skeleton.bones[Number(parsed.objectIndex)];
                }
                if (!(target instanceof Object3D)) {
                    console.warn(`[mesh] Animation track "${track.name}" has no target in "${root.name}"`);
                    return undefined;
                }
                if (!relatedNodes.has(target))
                    return undefined;
                const objectPath = parsed.objectName && parsed.objectName !== "bones"
                    ? `.${parsed.objectName}${parsed.objectIndex !== undefined ? `[${parsed.objectIndex}]` : ""}`
                    : "";
                const propertyIndex = parsed.propertyIndex !== undefined ? `[${parsed.propertyIndex}]` : "";
                return { track, target, property: `${objectPath}.${parsed.propertyName}${propertyIndex}` };
            }).filter((track) => track !== undefined)
        })).filter(({ tracks }) => tracks.length > 0);
        if (relevantClips.length === 0 && !(piece instanceof SkinnedMesh))
            return undefined;
        const needsHierarchy = piece instanceof SkinnedMesh || relevantClips.some(({ tracks }) => tracks.some(({ target }) => target !== piece));
        const sourceRoot = needsHierarchy ? root : piece;
        const clonedRoot = needsHierarchy ? cloneSkeleton(root) : piece.clone(false);
        const cloneMap = new Map();
        const mapNodes = (source, clone) => {
            cloneMap.set(source, clone);
            for (let i = 0; i < clone.children.length; i += 1) {
                mapNodes(source.children[i], clone.children[i]);
            }
        };
        mapNodes(sourceRoot, clonedRoot);
        const animatedPiece = cloneMap.get(piece);
        if (!animatedPiece) {
            throw new Error(`[mesh] Animation hierarchy does not contain piece "${piece.name}"`);
        }
        const clips = relevantClips.map(({ clip, tracks }) => {
            const filteredClip = new AnimationClip(clip.name, clip.duration, tracks.map(({ track, target, property }) => {
                const clone = cloneMap.get(target);
                if (!clone)
                    throw new Error(`[mesh] Animation target "${target.name}" is outside the cloned hierarchy`);
                const clonedTrack = track.clone();
                clonedTrack.name = `${clone.uuid}${property}`;
                return clonedTrack;
            }), clip.blendMode);
            return filteredClip;
        });
        // Model transforms already contain the imported node's bind transform.
        clonedRoot.updateMatrixWorld(true);
        const inverseBindTransform = new Matrix4().copy(animatedPiece.matrixWorld).invert();
        return new MeshPieceAnimation(clonedRoot, animatedPiece, clips, inverseBindTransform, relevantClips.map(({ clip }) => `clip:${root.animations.indexOf(clip)}`), relevantClips.map(({ clip }) => root.animations.slice(0, root.animations.indexOf(clip)).filter((previous) => previous.name === clip.name).length));
    }
    update(deltaSeconds) {
        this.playback.update(deltaSeconds);
        this.context.matrixWorldNeedsUpdate = true;
        this.context.updateWorldMatrix(true, true);
        const parent = this.context.parent;
        if (!parent)
            throw new Error("[mesh] Animation context must be attached before updating");
        this.inverseParent.copy(parent.matrixWorld).invert();
        this.mesh.updateMatrix();
        this.mesh.matrix.premultiply(this.animatedPiece.matrixWorld).premultiply(this.inverseParent);
        this.mesh.matrixWorldNeedsUpdate = true;
        if (this.mesh instanceof Mesh && this.animatedPiece instanceof Mesh &&
            this.mesh.morphTargetInfluences && this.animatedPiece.morphTargetInfluences) {
            for (let i = 0; i < this.animatedPiece.morphTargetInfluences.length; i += 1) {
                this.mesh.morphTargetInfluences[i] = this.animatedPiece.morphTargetInfluences[i];
            }
        }
    }
    dispose() {
        this.playback.dispose();
        this.context.traverse((node) => {
            if (node instanceof SkinnedMesh)
                node.skeleton.dispose();
        });
        this.context.removeFromParent();
    }
}
